import React, {Fragment, useEffect, useState, useContext} from 'react';
import {Tabs, Breadcrumb, Tooltip, Row, Col, Tag, Badge, Space, Descriptions, Form, Select, Input, Divider, Typography, Checkbox} from 'antd';
import {useRouteMatch, Link} from 'react-router-dom';
import {extract} from '../../utils/openapi-extractor';
import {EyeOutlined, CodeOutlined, MacCommandOutlined} from '@ant-design/icons';
import {TechnicalEditor} from '../technical-editor';
import {Block} from '../block';
import {OpenApiContext} from '../../openapi-context';
import {AppContext} from '../../state';
import {LintView} from '../lint-view';
import {errorsForModel} from '../../utils/openapi-schema-validator';
import {MarkdownView} from '../markdown-block';
import {MarkdownEditor} from '../markdown-editor';
import {cloneDeep, remove, get, unset, set} from 'lodash';
import trim from 'trim';

function ComponentName({model}) {
    return (
        <h2>
            <Space>
                <span>{model.name}</span>
                <Tag color="orange">security</Tag>
            </Space>
        </h2>
    );
}

function SecuritySchemeEditor({securityScheme}) {
    const [form] = Form.useForm();
    const [type, setType] = useState();
    const [httpScheme, setHttpScheme] = useState();
    const [supportedFlows, setSupportedFlows] = useState([]);
    const [oauthScopes, setOAuthScopes] = useState({});

    const changeDescription = (val) => (securityScheme.spec.description = val);
    const changeName = (e) => (securityScheme.spec.name = e.target.value);
    const changeType = (val) => {
        securityScheme.spec = {type: val};
        form.resetFields();
        setType(val);
        if (val !== 'http') {
            setHttpScheme(null);
        }
    };
    const changeIn = (val) => (securityScheme.spec.in = val);
    const changeHttpScheme = (val) => {
        securityScheme.spec.scheme = val;
        setHttpScheme(val);
    };
    const changeBearerFormat = (e) => (securityScheme.spec.bearerFormat = e.target.value);
    const changeOpenIdConnectUrl = (e) => (securityScheme.spec.openIdConnectUrl = e.target.value);
    const toggleFlow = (type) => (e) => {
        if (e.target.checked) {
            securityScheme.spec.flows = securityScheme.spec.flows || {};
            securityScheme.spec.flows[type] = {};
            setSupportedFlows([...supportedFlows, type]);
        } else {
            unset(securityScheme.spec.flows, type);
            remove(supportedFlows, (f) => f === type);
            setSupportedFlows([...supportedFlows]);
        }
    };
    const changeFlow = (type, field) => (e) => {
        set(securityScheme.spec, `flows.${type}.${field}`, e.target.value);
    };
    const changeScope = (type) => (e) => {
        const scopeString = e.target.value;

        if (scopeString && scopeString.length > 0) {
            securityScheme.spec.flows[type] = securityScheme.spec.flows[type] || {};
            securityScheme.spec.flows[type].scopes = scopeString
                .split(',')
                .map(trim)
                .reduce((scopes, entry) => {
                    scopes[entry] = entry;
                    return scopes;
                }, {});
        } else {
            securityScheme.spec.flows[type].scopes = {};
        }

        oauthScopes[type] = e.target.value;
        setOAuthScopes({...oauthScopes});
    };

    useEffect(() => {
        if (securityScheme) {
            setType(securityScheme.spec.type);
            setHttpScheme(securityScheme.spec.scheme);

            if (securityScheme.spec.type === 'oauth2') {
                const scopes = {};
                if (get(securityScheme.spec, 'flows.implicit')) {
                    supportedFlows.push('implicit');
                    if (securityScheme.spec.flows.implicit.scopes) {
                        scopes.implicit = Object.keys(securityScheme.spec.flows.implicit.scopes || {}).join(',');
                    } else {
                        scopes.implicit = '';
                    }
                }
                if (get(securityScheme.spec, 'flows.password')) {
                    supportedFlows.push('password');
                    if (securityScheme.spec.flows.password.scopes) {
                        scopes.password = Object.keys(securityScheme.spec.flows.password.scopes || {}).join(',');
                    } else {
                        scopes.password = '';
                    }
                }
                if (get(securityScheme.spec, 'flows.clientCredentials')) {
                    supportedFlows.push('clientCredentials');
                    if (securityScheme.spec.flows.clientCredentials.scopes) {
                        scopes.clientCredentials = Object.keys(securityScheme.spec.flows.clientCredentials.scopes || {}).join(',');
                    } else {
                        scopes.clientCredentials = '';
                    }
                }
                if (get(securityScheme.spec, 'flows.authorizationCode')) {
                    supportedFlows.push('authorizationCode');
                    if (securityScheme.spec.flows.authorizationCode.scopes) {
                        scopes.authorizationCode = Object.keys(securityScheme.spec.flows.authorizationCode.scopes || {}).join(',');
                    } else {
                        scopes.authorizationCode = '';
                    }
                }
                setOAuthScopes({...scopes});
                setSupportedFlows([...supportedFlows]);
            }
        }
    }, [securityScheme]);

    return securityScheme ? (
        <Fragment>
            <Typography.Title level={4}>Description</Typography.Title>
            <MarkdownEditor value={securityScheme.spec.description} onChange={changeDescription} height={175} />
            <Divider />
            <Form form={form} initialValues={securityScheme.spec}>
                <Form.Item label="Type" name="type">
                    <Select style={{width: '100%'}} onChange={changeType}>
                        <Select.Option value="apiKey">apiKey</Select.Option>
                        <Select.Option value="http">http</Select.Option>
                        <Select.Option value="oauth2">oauth2</Select.Option>
                        <Select.Option value="openIdConnect">openIdConnect</Select.Option>
                    </Select>
                </Form.Item>
                {type === 'apiKey' && (
                    <Fragment>
                        <Form.Item label="Name" name="name">
                            <Input onChange={changeName} />
                        </Form.Item>
                        <Form.Item label="In" name="in">
                            <Select style={{width: '100%'}} onChange={changeIn}>
                                <Select.Option value="query">query</Select.Option>
                                <Select.Option value="header">header</Select.Option>
                                <Select.Option value="cookie">cookie</Select.Option>
                            </Select>
                        </Form.Item>
                    </Fragment>
                )}
                {type === 'http' && (
                    <Form.Item label="Scheme" name="scheme">
                        <Select style={{width: '100%'}} onChange={changeHttpScheme}>
                            <Select.Option value="basic">Basic</Select.Option>
                            <Select.Option value="bearer">Bearer</Select.Option>
                            <Select.Option value="digest">Digest</Select.Option>
                            <Select.Option value="HOBA">HOBA</Select.Option>
                            <Select.Option value="mutual">Mutual</Select.Option>
                            <Select.Option value="negotiate">Negotiate</Select.Option>
                            <Select.Option value="OAuth">OAuth</Select.Option>
                            <Select.Option value="SCRAM-SHA-1">SCRAM-SHA-1</Select.Option>
                            <Select.Option value="SCRAM-SHA-256">SCRAM-SHA-256</Select.Option>
                            <Select.Option value="vapid">vapid</Select.Option>
                        </Select>
                    </Form.Item>
                )}
                {httpScheme === 'bearer' && (
                    <Form.Item label="Bearer Format" name="bearerFormat">
                        <Input onChange={changeBearerFormat} />
                    </Form.Item>
                )}
                {type === 'openIdConnect' && (
                    <Form.Item label="openIDConnect Url" name="openIdConnectUrl">
                        <Input onChange={changeOpenIdConnectUrl} />
                    </Form.Item>
                )}
                {type === 'oauth2' && (
                    <Fragment>
                        <Typography.Title level={3}>OAuth Flows</Typography.Title>
                        <Typography.Paragraph>Check the supported flows and fill-up their properties</Typography.Paragraph>
                        <Form.Item label="Implicit Flow">
                            <Checkbox defaultChecked={supportedFlows.find((f) => f === 'implicit')} onChange={toggleFlow('implicit')} />
                        </Form.Item>
                        {supportedFlows.find((f) => f === 'implicit') && (
                            <Fragment>
                                <Form.Item label="authorizationUrl">
                                    <Input defaultValue={securityScheme.spec.flows.implicit.authorizationUrl} onChange={changeFlow('implicit', 'authorizationUrl')} />
                                </Form.Item>
                                <Form.Item label="refreshUrl">
                                    <Input defaultValue={securityScheme.spec.flows.implicit.refreshUrl} onChange={changeFlow('implicit', 'refreshUrl')} />
                                </Form.Item>
                                <Form.Item label="scopes">
                                    <Input defaultValue={oauthScopes.implicit} onChange={changeScope('implicit')} />
                                    <p>Coma-separated list of scopes. ex: manage:user,read:account</p>
                                </Form.Item>
                            </Fragment>
                        )}
                        <Form.Item label="Password Flow">
                            <Checkbox defaultChecked={supportedFlows.find((f) => f === 'password')} onChange={toggleFlow('password')} />
                        </Form.Item>
                        {supportedFlows.find((f) => f === 'password') && (
                            <Fragment>
                                <Form.Item label="tokenUrl">
                                    <Input defaultValue={securityScheme.spec.flows.password.tokenUrl} onChange={changeFlow('password', 'tokenUrl')} />
                                </Form.Item>
                                <Form.Item label="refreshUrl">
                                    <Input defaultValue={securityScheme.spec.flows.password.refreshUrl} onChange={changeFlow('password', 'refreshUrl')} />
                                </Form.Item>
                                <Form.Item label="scopes">
                                    <Input defaultValue={oauthScopes.password} onChange={changeScope('password')} />
                                    <p>Coma-separated list of scopes. ex: manage:user,read:account</p>
                                </Form.Item>
                            </Fragment>
                        )}
                        <Form.Item label="Client Credentials Flow">
                            <Checkbox defaultChecked={supportedFlows.find((f) => f === 'clientCredentials')} onChange={toggleFlow('clientCredentials')} />
                        </Form.Item>
                        {supportedFlows.find((f) => f === 'clientCredentials') && (
                            <Fragment>
                                <Form.Item label="tokenUrl">
                                    <Input defaultValue={securityScheme.spec.flows.clientCredentials.tokenUrl} onChange={changeFlow('clientCredentials', 'tokenUrl')} />
                                </Form.Item>
                                <Form.Item label="refreshUrl">
                                    <Input defaultValue={securityScheme.spec.flows.clientCredentials.refreshUrl} onChange={changeFlow('clientCredentials', 'refreshUrl')} />
                                </Form.Item>
                                <Form.Item label="scopes">
                                    <Input defaultValue={oauthScopes.clientCredentials} onChange={changeScope('clientCredentials')} />
                                    <p>Coma-separated list of scopes. ex: manage:user,read:account</p>
                                </Form.Item>
                            </Fragment>
                        )}
                        <Form.Item label="Authorization Code Flow">
                            <Checkbox defaultChecked={supportedFlows.find((f) => f === 'authorizationCode')} onChange={toggleFlow('authorizationCode')} />
                        </Form.Item>
                        {supportedFlows.find((f) => f === 'authorizationCode') && (
                            <Fragment>
                                <Form.Item label="authorizationUrl">
                                    <Input defaultValue={securityScheme.spec.flows.authorizationCode.authorizationUrl} onChange={changeFlow('authorizationCode', 'authorizationUrl')} />
                                </Form.Item>
                                <Form.Item label="tokenUrl">
                                    <Input defaultValue={securityScheme.spec.flows.authorizationCode.tokenUrl} onChange={changeFlow('authorizationCode', 'tokenUrl')} />
                                </Form.Item>
                                <Form.Item label="refreshUrl">
                                    <Input defaultValue={securityScheme.spec.flows.authorizationCode.refreshUrl} onChange={changeFlow('authorizationCode', 'refreshUrl')} />
                                </Form.Item>
                                <Form.Item label="scopes">
                                    <Input defaultValue={oauthScopes.authorizationCode} onChange={changeScope('authorizationCode')} />
                                    <p>Coma-separated list of scopes. ex: manage:user,read:account</p>
                                </Form.Item>
                            </Fragment>
                        )}
                    </Fragment>
                )}
            </Form>
        </Fragment>
    ) : null;
}
export const SecuritySchemeView = function ({spec, dispatch, mobile}) {
    const app = useContext(AppContext);
    const {state, SUPPORTED_EDITOR_ROLES} = useContext(OpenApiContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/securitySchemes/:modelKey');
    const [model, updateModel] = useState();
    const [errorCount, setErrorCount] = useState(0);
    const [editMode, setEditMode] = useState();
    const [editedSecurityScheme, setEditedSecurityScheme] = useState();

    useEffect(() => {
        updateModel(extract(spec).securityScheme(params.modelKey));
        app.dispatch({
            type: 'add-breadcrumb-entry',
            entry: {
                env: params.env,
                linkType: 'securitySchemes',
                category: params.category,
                key: params.key,
                version: params.version,
                modelKey: params.modelKey,
            },
        });
    }, [spec, params.modelKey]);

    useEffect(() => {
        if (model) {
            setErrorCount(errorsForModel(state.specValidationErrors, model.name).length);
        } else {
            setErrorCount(0);
        }
    }, [spec, params.modelKey, model, state.specValidationErrors]);

    function onTechnicalEdit(newModel) {
        dispatch({
            type: 'update-security-scheme-def',
            securityScheme: {name: model.name, spec: newModel},
        });
    }

    const canEdit = () => SUPPORTED_EDITOR_ROLES.indexOf(state.role) !== -1 && !mobile;

    function triggerEdit(editing) {
        setEditMode(editing);
        if (editing) {
            setEditedSecurityScheme(cloneDeep(model));
        } else {
            setEditedSecurityScheme(null);
        }
    }

    function handleSave() {
        dispatch({type: 'update-security-scheme-def', securityScheme: editedSecurityScheme});
    }

    return model ? (
        <Fragment>
            <Row>
                <Col xs={24}>
                    <div className="breadcrumbContainer">
                        <Breadcrumb separator=">">
                            {app.state.breadcrumb.length > 1 &&
                                app.state.breadcrumb.map((entry) => (
                                    <Breadcrumb.Item key={entry.url}>
                                        <Tooltip title={`Jump to ${entry.category}/${entry.key}`}>
                                            <Link to={entry.url}>
                                                {entry.title}[{entry.version}]
                                            </Link>
                                        </Tooltip>
                                    </Breadcrumb.Item>
                                ))}
                        </Breadcrumb>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Tabs
                        type="card"
                        tabPlacement="right"
                        id="modelTabs"
                        items={[
                            {
                                key: 'visual',
                                label: <EyeOutlined />,
                                children: (
                                    <div className="scrollableViewer">
                                        <Block noEdit={!canEdit()} onEdit={triggerEdit} onSave={handleSave}>
                                    <ComponentName model={model} />
                                    {!editMode ? (
                                        <Fragment>
                                            <MarkdownView source={model.spec.description} />
                                            <Descriptions bordered column={{xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1}}>
                                                <Descriptions.Item label="Type">{model.spec.type}</Descriptions.Item>
                                                {model.spec.type === 'apiKey' && <Descriptions.Item label="Name">{model.spec.name}</Descriptions.Item>}
                                                {model.spec.type === 'apiKey' && <Descriptions.Item label="In">{model.spec.in}</Descriptions.Item>}
                                                {model.spec.type === 'http' && <Descriptions.Item label="Scheme">{model.spec.scheme}</Descriptions.Item>}
                                                {model.spec.type === 'http' && model.spec.scheme === 'bearer' && <Descriptions.Item label="Bearer Format">{model.spec.bearerFormat}</Descriptions.Item>}
                                                {model.spec.type === 'openIdConnect' && <Descriptions.Item label="openIdConnectUrl">{model.spec.openIdConnectUrl}</Descriptions.Item>}
                                            </Descriptions>
                                            {model.spec.type === 'oauth2' && get(model.spec, 'flows.implicit') && (
                                                <Descriptions title="Implicit Flow" bordered column={{xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1}}>
                                                    <Descriptions.Item label="authorizationUrl">{model.spec.flows.implicit.authorizationUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="refreshUrl">{model.spec.flows.implicit.refreshUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="scopes">{Object.keys(model.spec.flows.implicit.scopes || {}).join(',')}</Descriptions.Item>
                                                </Descriptions>
                                            )}
                                            {model.spec.type === 'oauth2' && get(model.spec, 'flows.password') && (
                                                <Descriptions title="Password Flow" bordered column={{xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1}}>
                                                    <Descriptions.Item label="tokenUrl">{model.spec.flows.password.tokenUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="refreshUrl">{model.spec.flows.password.refreshUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="scopes">{Object.keys(model.spec.flows.password.scopes || {}).join(',')}</Descriptions.Item>
                                                </Descriptions>
                                            )}
                                            {model.spec.type === 'oauth2' && get(model.spec, 'flows.clientCredentials') && (
                                                <Descriptions title="Client Credentials Flow" bordered column={{xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1}}>
                                                    <Descriptions.Item label="tokenUrl">{model.spec.flows.clientCredentials.tokenUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="refreshUrl">{model.spec.flows.clientCredentials.refreshUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="scopes">{Object.keys(model.spec.flows.clientCredentials.scopes || {}).join(',')}</Descriptions.Item>
                                                </Descriptions>
                                            )}
                                            {model.spec.type === 'oauth2' && get(model.spec, 'flows.authorizationCode') && (
                                                <Descriptions title="Authorization Code Flow" bordered column={{xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1}}>
                                                    <Descriptions.Item label="authorizationUrl">{model.spec.flows.authorizationCode.authorizationUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="tokenUrl">{model.spec.flows.authorizationCode.tokenUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="refreshUrl">{model.spec.flows.authorizationCode.refreshUrl}</Descriptions.Item>
                                                    <Descriptions.Item label="scopes">{Object.keys(model.spec.flows.authorizationCode.scopes || {}).join(',')}</Descriptions.Item>
                                                </Descriptions>
                                            )}
                                        </Fragment>
                                    ) : (
                                        <SecuritySchemeEditor securityScheme={editedSecurityScheme} />
                                    )}
                                        </Block>
                                    </div>
                                ),
                            },
                            {
                                key: 'technical',
                                label: <CodeOutlined />,
                                disabled: state.editMode,
                                children: (
                                    <>
                                        <ComponentName model={model} />
                                        <Block noEdit>
                                            <TechnicalEditor object={model.spec} update={onTechnicalEdit} />
                                        </Block>
                                    </>
                                ),
                            },
                            {
                                key: 'lint',
                                label: errorCount > 0 ? (
                                    <Badge count={errorCount}>
                                        <MacCommandOutlined />
                                    </Badge>
                                ) : (
                                    <MacCommandOutlined />
                                ),
                                disabled: state.editMode,
                                children: (
                                    <Block noEdit>
                                        <LintView type="models" componentType="securitySchemes" modelKey={model.name} />
                                    </Block>
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </Fragment>
    ) : null;
};
